import type { ReactNode } from 'react';
import { Box, Flex, Panel } from '@customerio/pluma-components/react';
import { MinimalAppHeader } from './minimal-app-header';
import * as styles from './auth-form-layout.css';

type AuthFormLayoutProps = {
  children: ReactNode;
  /**
   * Split-screen content rendered beside the form (login index's referral
   * splash). Omitted → centered single panel.
   */
  splash?: ReactNode;
  /** Show the header's "Start your free trial" callout (login index only). */
  showSignupCallout?: boolean;
};

/**
 * Page shell for the unauthenticated auth pages (login, join-request): the
 * minimal logo header plus the current step. With `splash` it uses Ember's
 * split-screen layout (form left, splash right); otherwise a centered panel.
 */
export function AuthFormLayout({ children, splash, showSignupCallout = false }: AuthFormLayoutProps) {
  // Treat null/false (from `cond && <Splash />` or `cond ? <Splash /> : null`)
  // as "no splash" — only a real node opens the split layout, otherwise an
  // empty teal pane would show.
  if (splash != null && splash !== false) {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <MinimalAppHeader showSignupCallout={showSignupCallout} />
        <div className={styles.splitMain}>
          <div className={styles.splitLeft}>
            <Box className={styles.container}>
              <Panel>{children}</Panel>
              {/* Mount point where the in-app-message plugin injects login-page
                  messages, matching Ember's AuthFormLayout. */}
              <Box id="in-app-target_login" mt="300" />
            </Box>
          </div>
          <div className={styles.splitRight}>{splash}</div>
        </div>
      </Box>
    );
  }

  return (
    <Box style={{ minHeight: '100vh' }}>
      <MinimalAppHeader showSignupCallout={showSignupCallout} />
      <Flex justifyContent="center" px="200" pt="500">
        <Box className={styles.container}>
          <Panel>{children}</Panel>
          {/* In-app-message mount for the non-index auth steps, matching
              Ember's AuthFormLayout. */}
          <Box id="in-app-target" mt="300" />
        </Box>
      </Flex>
    </Box>
  );
}
